import {
  ChevronLeftIcon,
  ChevronRightIcon
} from 'lucide-react'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDebounceCallback } from 'usehooks-ts'

import { events, PRODUCT_CATEGORIES, SessionContext } from '../shared'
import { type MarketplaceProductCategory } from '../shared/types'

export const CategoryFilter = ({ activeCategory, setActiveCategory }: { activeCategory: MarketplaceProductCategory, setActiveCategory: (category: MarketplaceProductCategory) => void }) => {
  const [showLeftChevron, setShowLeftChevron] = useState<boolean>(false)
  const [showRightChevron, setShowRightChevron] = useState<boolean>(false)
  const [categoryHistory, setCategoryHistory] = useState<MarketplaceProductCategory[]>([activeCategory])
  const [isMdOrLarger, setIsMdOrLarger] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const member = useContext(SessionContext)

  const debouncedCheckOverflow = useDebounceCallback(() => {
    const container = scrollContainerRef.current
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container

      // Use a small tolerance to avoid floating point issues
      const tolerance = 2
      setShowLeftChevron(scrollLeft > tolerance)
      setShowRightChevron(scrollLeft + clientWidth < scrollWidth - tolerance)
    } else {
      setShowLeftChevron(false)
      setShowRightChevron(false)
    }
  }, 50)

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    // Initial check
    debouncedCheckOverflow()

    window.addEventListener('resize', debouncedCheckOverflow)
    container.addEventListener('scroll', debouncedCheckOverflow)

    const mediaQuery = window.matchMedia('(min-width: 768px)')
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsMdOrLarger(event.matches)
    }

    // Set initial state before listener has a chance to kick in
    setIsMdOrLarger(mediaQuery.matches)

    mediaQuery.addEventListener('change', handleMediaQueryChange)

    return () => {
      window.removeEventListener('resize', debouncedCheckOverflow)
      container.removeEventListener('scroll', debouncedCheckOverflow)
      mediaQuery.removeEventListener('change', handleMediaQueryChange)
    }
  }, [debouncedCheckOverflow])

  const handleCategoryClick = (category: MarketplaceProductCategory) => {
    events.emitCategorySelectedEvent(member, category, PRODUCT_CATEGORIES.map(c => c.category), categoryHistory)
    setActiveCategory(category)
    setCategoryHistory([...categoryHistory, category])
  }

  const scrollPrev = () => {
    const container = scrollContainerRef.current
    if (container) {
      container.scrollBy({ left: -container.clientWidth, behavior: 'smooth' })
    }
  }

  const scrollNext = () => {
    const container = scrollContainerRef.current
    if (container) {
      container.scrollBy({ left: container.clientWidth, behavior: 'smooth' })
    }
  }

  return (
    <div className='relative flex items-center mt-10 mb-6'>
      <button
        type='button'
        aria-label='Previous categories'
        onClick={scrollPrev}
        className={`hidden @md:inline-flex flex-shrink-0 ${baseButtonStyles} ${inactiveStyles} overflow-hidden transition-[opacity,max-width,padding,margin] duration-300 ease-in-out ${
          showLeftChevron
            ? 'opacity-100 max-w-[2.5rem] w-8 !p-2 mr-3 border'
            : 'opacity-0 max-w-0 !p-0 !m-0 border-none pointer-events-none'
        }`}
        tabIndex={showLeftChevron ? 0 : -1}
      >
        <ChevronLeftIcon size={iconSize} />
      </button>
      <div
        ref={scrollContainerRef}
        className='flex items-center gap-2 overflow-x-auto scroll-smooth no-scrollbar [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] -mx-4 px-4 @md:mx-0 @md:px-0'
        style={isMdOrLarger ? getMaskStyle(showLeftChevron, showRightChevron) : { maskImage: 'none', WebkitMaskImage: 'none' }}
      >
        {PRODUCT_CATEGORIES.map(({ category, displayName, Icon }) => {
          const isActive = activeCategory === category
          return (
            <button
              key={category}
              type='button'
              onClick={() => handleCategoryClick(category)}
              className={`${baseButtonStyles} ${isActive ? activeStyles : inactiveStyles}`}
            >
              <Icon size={iconSize} className={`${isActive ? 'text-thatch-600' : 'text-gray-500'}`} />
              <span>{displayName}</span>
            </button>
          )
        })}
      </div>
      <button
        type='button'
        aria-label='More categories'
        onClick={scrollNext}
        className={`hidden @md:inline-flex flex-shrink-0 ${baseButtonStyles} ${inactiveStyles} overflow-hidden transition-[opacity,max-width,padding,margin] duration-300 ease-in-out ${
          showRightChevron
            ? 'opacity-100 max-w-[2.5rem] w-8 !p-2 ml-3 border'
            : 'opacity-0 max-w-0 !p-0 !m-0 border-none pointer-events-none'
        }`}
        tabIndex={showRightChevron ? 0 : -1}
      >
        <ChevronRightIcon size={iconSize} />
      </button>
    </div>
  )
}

const baseButtonStyles = 'flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-full body px-4 h-10 transition focus:outline-none focus-visible:outline-thatch-500 focus-visible:outline-offset-[-2px] h-8'
const activeStyles = 'bg-thatch-100 text-thatch-600 border border-thatch-500 font-semibold'
const inactiveStyles = 'bg-white border border-gray-200 text-gray-800 hover:bg-gray-100 focus-visible:bg-gray-100 font-normal'

const iconSize = 16

const getMaskStyle = (showLeftChevron: boolean, showRightChevron: boolean) => {
  const fadeWidth = '52px'
  const createMask = (value: string) => ({ maskImage: value, WebkitMaskImage: value })

  if (showLeftChevron && showRightChevron) {
    return createMask(`linear-gradient(to right, transparent, black ${fadeWidth}, black calc(100% - ${fadeWidth}), transparent)`)
  } else if (showLeftChevron) {
    return createMask(`linear-gradient(to right, transparent, black ${fadeWidth})`)
  } else if (showRightChevron) {
    return createMask(`linear-gradient(to right, black calc(100% - ${fadeWidth}), transparent)`)
  } else {
    return createMask('none')
  }
}
